import axios from 'helpers/api';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import { toast } from 'react-toastify';
import paths from 'routes/paths';

// The API (and the row object passed through router state) is inconsistent about
// key names and about how "empty" is represented ("null" string, null, undefined).
// Everything is funnelled through here so the form always has the same shape.
const text = (...values) => {
    for (const value of values) {
        if (value === undefined || value === null) continue;
        const str = String(value).trim();
        if (str === '' || str === 'null' || str === 'undefined') continue;
        return str;
    }
    return '';
};

// US numbers are held in state as bare digits and only formatted for display,
// so whatever shape the API sends back ("5551234567", "555-123-4567",
// "+1 (555) 123-4567") renders the same and posts back clean.
const US_PHONE_MASK = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

const toPhoneDigits = (value) => {
    let digits = String(value ?? '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    return digits.slice(0, 10);
};

const flag = (...values) => {
    for (const value of values) {
        if (value === undefined || value === null) continue;
        return Number(value) === 1 ? 1 : 0;
    }
    return 0;
};

const normalizeFacility = (raw = {}) => ({
    id: raw.id ?? raw.facility_id ?? '',
    group_id: raw.group_id ?? '',
    facility_name: text(raw.facility_name, raw.name),
    email: text(raw.email),
    admin_email: text(raw.facilityadminemail, raw.admin_email, raw.facility_admin_email),
    mobile: toPhoneDigits(text(raw.mobile, raw.phone)),
    fax: text(raw.fax),
    is_email: flag(raw.is_email),
    is_fax: flag(raw.is_fax),
    is_sms: flag(raw.is_sms),
    is_liaison: flag(raw.is_liaison),
    is_medpass: flag(raw.is_medpass),
    cycle_leed_day: text(raw.cycle_leed_day),
    delivery_time:
        raw.delivery_time === undefined || raw.delivery_time === null || raw.delivery_time === ''
            ? ''
            : String(raw.delivery_time)
});

const EditFacilityLookUpForm = () => {
    const location = useLocation();
    const data = location?.state?.obj || {};
    const [formData, setFormData] = useState(() => normalizeFacility(data));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [mobileError, setMobileError] = useState('');
    const [leadDays] = useState([
        { key: '1', value: '1' },
        { key: '2', value: '2' },
        { key: '3', value: '3' },
        { key: '4', value: '4' },
        { key: '5', value: '5' },
        { key: '6', value: '6' }
    ]);
    const [deliveryTime] = useState([
        {
            "key": "03:00PM - 08:00PM",
            "value": 0
        },
        {
            "key": "10:00AM - 03:00PM",
            "value": 1
        },
    ]);

    const facilityId = data?.id ?? data?.facility_id;
    const groupId = data?.group_id;

    // The table row only carries a handful of columns, so the contact fields
    // (email / admin email / mobile / fax) arrive empty. Pull the full facility
    // record for the group and merge it in so every field is prepopulated.
    useEffect(() => {
        if (!facilityId || !groupId) return;

        let cancelled = false;

        const fetchFacility = async () => {
            setLoading(true);
            try {
                const tokenStr = localStorage.getItem("accessToken");
                const { data: response } = await axios.get(
                    `${process.env.REACT_APP_SERVER_URL}/facilities/list-facility/${groupId}`,
                    { headers: { 'Authorization': `Bearer ${tokenStr}` } }
                );

                const facilities = Object.values(response?.data || {});
                const current = facilities.find(
                    (facility) => Number(facility?.id) === Number(facilityId)
                );

                if (!cancelled && current) {
                    // Router state first, API record on top: the API is the source
                    // of truth, but anything it omits keeps the row's value.
                    setFormData(normalizeFacility({ ...data, ...current }));
                }
            } catch (error) {
                if (!cancelled) {
                    toast.error("Unable to load facility details");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchFacility();
        return () => {
            cancelled = true;
        };
    }, [facilityId, groupId]);

    const handleSubmit = async e => {
        e.preventDefault()

        // Optional field, but if it is filled in it has to be a full US number.
        if (formData.mobile && formData.mobile.length !== 10) {
            setMobileError("Please enter a 10 digit US mobile number.");
            return;
        }
        setMobileError('');

        let tokenStr = localStorage.getItem("accessToken");
        let objData = {
            facility_id: formData.id,
            is_liaison: formData.is_liaison,
            is_email: formData.is_email,
            email: formData.email,
            admin_email: formData.admin_email,
            is_fax: formData.is_fax,
            is_sms: formData.is_sms,
            is_medpass: formData.is_medpass,
            fax: formData.fax,
            phone: formData.mobile,
            cycle_leed_day: formData.cycle_leed_day,
            delivery_time: formData.delivery_time
        }
        setSaving(true);
        try {
            const { data: response } = await axios.post(`${process.env.REACT_APP_SERVER_URL}` + "/facilities/update-facility", objData, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
            if (response.status) {
                toast.success("Group Details Updated successfully");
            }
            else {
                toast.info("Error");
            }
        } catch (error) {
            toast.error("Something went wrong");
        } finally {
            setSaving(false);
        }
    }

    const handleFieldChange = e => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleMobileChange = e => {
        setMobileError('');
        setFormData({
            ...formData,
            mobile: toPhoneDigits(e.target.value)
        });
    };

    const breadcrumbItems = [
        { name: 'Dashboard', href: '/main', isActive: false },
        { name: 'Groups&Facility', href: paths.faciliityLookUp, isActive: false },
        { name: 'Group-Profile', href: `${paths.facilitylistlookUp}/${formData.group_id}`, isActive: false },
        { name: 'Edit-Facility', href: paths.facilitylistlookUp, isActive: true },
    ];

    return (
        <>
        <CommonBreadCrumb breadcrumbItems = {breadcrumbItems} />
        <Form onSubmit={handleSubmit} name="FacilityLookUpForm">
            <Card className="mb-3">
                <Card.Body >
                    <Row className="mb-2">
                        <Form.Group>
                            <Form.Label>Facility</Form.Label>
                            <Form.Control
                                name="facility_name"
                                onChange={handleFieldChange}
                                size="sm"
                                disabled="disabled"
                                value={formData.facility_name}
                            >
                            </Form.Control>
                        </Form.Group>
                    </Row>
                    <Row className="mt-3">
                        <Col>
                            <Form.Check
                                type='switch'
                                name="is_email"
                                id='is_email'
                                checked={
                                    Number(formData.is_email) === 1
                                }
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        is_email: e.target.checked ? 1 : 0
                                    })
                                }
                                label='Email'
                            />
                        </Col>
                        <Col>
                            <Form.Check
                                type='switch'
                                name="is_fax"
                                id='is_fax'
                                checked={
                                    Number(formData.is_fax) === 1
                                }
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        is_fax: e.target.checked ? 1 : 0
                                    })
                                }
                                label='Fax'
                            />
                        </Col>
                        <Col>
                            <Form.Check
                                type='switch'
                                name="is_sms"
                                id='is_sms'
                                checked={
                                    Number(formData.is_sms) === 1
                                }
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        is_sms: e.target.checked ? 1 : 0
                                    })
                                }
                                label='SMS'
                            />
                        </Col>
                        <Col>
                            <Form.Check
                                type='switch'
                                name="is_liaison"
                                id='is_liaison'
                                checked={
                                    Number(formData.is_liaison) === 1
                                }
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        is_liaison: e.target.checked ? 1 : 0
                                    })
                                }
                                label='Liaison'
                            />
                        </Col>
                        <Col>
                            <Form.Check
                                type='switch'
                                name="is_medpass"
                                id='is_medpass'
                                checked={
                                    Number(formData.is_medpass) === 1
                                }
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        is_medpass: e.target.checked ? 1 : 0
                                    })
                                }
                                label='Med Pass'
                            />
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col>
                            <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    placeholder='Email'
                                    value={formData.email}
                                    name="email"
                                    onChange={handleFieldChange}
                                    type="text"
                                    size="sm"
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>Facility Admin Email</Form.Label>
                                <Form.Control
                                    placeholder='Email'
                                    value={formData.admin_email}
                                    name="admin_email"
                                    onChange={handleFieldChange}
                                    type="text"
                                    size="sm"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col>
                            <Form.Group>
                                <Form.Label>Mobile Number</Form.Label>
                                <MaskedInput
                                    mask={US_PHONE_MASK}
                                    className={`form-control form-control-sm${mobileError ? ' is-invalid' : ''}`}
                                    guide={false}
                                    placeholder="(XXX) XXX-XXXX"
                                    onChange={handleMobileChange}
                                    type="tel"
                                    name="mobile"
                                    value={formData.mobile}
                                />
                                {mobileError && <small className="text-danger">{mobileError}</small>}
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>FAX</Form.Label>
                                <Form.Control
                                    placeholder=''
                                    value={formData.fax}
                                    name="fax"
                                    onChange={handleFieldChange}
                                    type="text"
                                    size="sm"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col>
                            <Form.Group>
                                <Form.Label>Lead Number of Day for Cycle Delivery</Form.Label>
                                <Form.Select
                                    name="cycle_leed_day"
                                    onChange={handleFieldChange}
                                    value={formData.cycle_leed_day}
                                    size="sm"
                                >
                                    <option value="">Select</option>
                                    {leadDays.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.key}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label>Delivery Time</Form.Label>
                                <Form.Select
                                    name="delivery_time"
                                    onChange={handleFieldChange}
                                    value={formData.delivery_time}
                                    size="sm"
                                >
                                    <option value="">Select</option>
                                    {deliveryTime.map((option) => (
                                        <option key={option.value} value={String(option.value)}>
                                            {option.key}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col></Col>
                        <Col>
                            <Form.Group>
                                <Button
                                    type="submit"
                                    color="primary"
                                    className="w-100 mt-4"
                                    disabled={loading || saving}>
                                    {saving ? <Spinner size="sm" animation="border" /> : "Save"}
                                </Button>
                            </Form.Group>
                        </Col>
                        <Col></Col>
                    </Row>
                </Card.Body>
            </Card>
        </Form>
        </>
    )
}

export default EditFacilityLookUpForm
